import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Phone, CalendarDays, CheckSquare, AlertCircle, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { fmtDateTime } from "@/lib/constants";

const ICON = { call: Phone, meeting: CalendarDays, task: CheckSquare };
const PRIORITY_RANK = { high: 0, medium: 1, low: 2 };
const PRIORITY_DOT = { high: "bg-rose-500", medium: "bg-amber-500", low: "bg-slate-300" };

export default function DailyTasks({ actions }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { data: activities = [] } = useQuery({
    queryKey: ["activities-today"],
    queryFn: async () => (await api.get("/activities", { params: { completed: false } })).data,
  });

  const toggle = useMutation({
    mutationFn: async ({ id, completed }) => (await api.patch(`/activities/${id}`, { completed })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["activities-today"] }); qc.invalidateQueries({ queryKey: ["activities"] }); qc.invalidateQueries({ queryKey: ["dashboard-stats"] }); },
  });
  const reprioritize = useMutation({
    mutationFn: async ({ id, priority }) => (await api.patch(`/activities/${id}`, { priority })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["activities-today"] }),
  });

  // Split: due today/overdue vs upcoming
  const todayEnd = new Date(); todayEnd.setHours(23, 59, 59, 999);
  const todayIso = todayEnd.toISOString();
  const todayTasks = activities.filter((a) => a.due_at && a.due_at <= todayIso);
  const upcoming = activities.filter((a) => !a.due_at || a.due_at > todayIso);

  // Sort by priority then by due_at
  const sortByPriority = (a, b) => {
    const p = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
    if (p !== 0) return p;
    return (a.due_at || "").localeCompare(b.due_at || "");
  };
  todayTasks.sort(sortByPriority);
  upcoming.sort(sortByPriority);

  const overdue = todayTasks.filter((a) => a.due_at && a.due_at < new Date().toISOString()).length;

  return (
    <Card className="rounded-sm border-slate-200 industrial-shadow overflow-hidden" data-testid="daily-tasks-widget">
      <div className="px-5 py-4 border-b border-slate-200 bg-slate-900 text-white flex items-center justify-between">
        <div>
          <div className="stamp-label text-cyan-400">Today</div>
          <div className="font-display text-lg font-semibold mt-0.5">Daily Checklist</div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="font-display text-2xl font-bold text-cyan-400">{todayTasks.length}</div>
            <div className="stamp-label text-slate-400 text-[9px]">{overdue > 0 && <span className="text-rose-400">{overdue} overdue · </span>}due today</div>
          </div>
          {actions && <div className="flex items-center gap-1.5">{actions}</div>}
        </div>
      </div>

      <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
        {todayTasks.length === 0 ? (
          <div className="p-8 text-center">
            <CheckSquare size={28} className="text-slate-300 mx-auto mb-2" />
            <div className="text-sm font-medium text-slate-700">All caught up</div>
            <div className="text-xs text-slate-500 mt-0.5">Nothing due today.</div>
          </div>
        ) : (
          todayTasks.map((a) => <TaskRow key={a.id} task={a} onToggle={toggle.mutate} onPriority={reprioritize.mutate} />)
        )}

        {upcoming.length > 0 && (
          <div className="bg-slate-50/50">
            <div className="px-4 py-2 stamp-label text-slate-500 border-b border-slate-100">Upcoming</div>
            {upcoming.slice(0, 5).map((a) => <TaskRow key={a.id} task={a} onToggle={toggle.mutate} onPriority={reprioritize.mutate} />)}
          </div>
        )}
      </div>

      <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
        <div className="text-xs text-slate-500">{activities.length} open · {todayTasks.filter(t => t.priority === "high").length} high-priority</div>
        <Button data-testid="view-all-activities-btn" size="sm" variant="ghost" onClick={() => nav("/tasks")} className="text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50 h-7">
          View all <ChevronRight size={13} className="ml-0.5" />
        </Button>
      </div>
    </Card>
  );
}

function TaskRow({ task, onToggle, onPriority }) {
  const Icon = ICON[task.type] || CheckSquare;
  const isOverdue = task.due_at && task.due_at < new Date().toISOString();
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 hover:bg-cyan-50/30 transition-colors group" data-testid={`daily-task-${task.id}`}>
      <Checkbox
        checked={task.completed}
        onCheckedChange={(v) => onToggle({ id: task.id, completed: !!v })}
        data-testid={`daily-toggle-${task.id}`}
      />
      <div className={`w-2 h-2 rounded-full shrink-0 ${PRIORITY_DOT[task.priority]}`} />
      <div className={`w-7 h-7 flex items-center justify-center shrink-0 ${isOverdue ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"}`}>
        <Icon size={13} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-900 truncate">{task.title}</div>
        <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
          {isOverdue && <AlertCircle size={10} className="text-rose-500" />}
          <span className={isOverdue ? "text-rose-600 font-medium" : ""}>{task.due_at ? fmtDateTime(task.due_at) : "No due date"}</span>
          {task.auto_generated && <Badge variant="outline" className="rounded-sm text-[8px] border-cyan-200 bg-cyan-50 text-cyan-700 h-3.5 px-1">AUTO</Badge>}
        </div>
      </div>
      <Select value={task.priority} onValueChange={(v) => onPriority({ id: task.id, priority: v })}>
        <SelectTrigger data-testid={`daily-priority-${task.id}`} className="h-7 w-[88px] rounded-sm text-[10px] uppercase tracking-wider font-semibold">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="high">🔴 High</SelectItem>
          <SelectItem value="medium">🟡 Medium</SelectItem>
          <SelectItem value="low">⚪ Low</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
